/**
 * Partner offers (REQ-025): a list of insurers' offers, in the order the
 * server sent them.
 *
 * What this must not do is as important as what it does. It shows no price,
 * score or "recommended" mark and does not reorder (AC-004); every card says
 * whose offer it is and that HealthPass is paid for it (AC-005); and the
 * button asks the server for the link, which carries only a click id and the
 * offer id, then opens it in a new tab with no opener and no referrer, so the
 * insurer does not even learn which trip page it came from (AC-006).
 *
 * Never used on the emergency card or a shared card view.
 */
import {
  clickPartnerOffer,
  offerDescription,
  type PartnerOffer,
} from "@/services/api/marketplace";
import { useI18nStore } from "@/stores/i18nStore";
import { ExternalLink } from "lucide-react";
import React, { useState } from "react";
import { toast } from "react-hot-toast";

interface PartnerOfferListProps {
  offers: PartnerOffer[];
  tripId: string;
}

const fill = (template: string, insurer: string) =>
  template.split("{insurer}").join(insurer);

export const PartnerOfferList: React.FC<PartnerOfferListProps> = ({
  offers,
  tripId,
}) => {
  const { t, language } = useI18nStore();
  const [opening, setOpening] = useState<string | null>(null);

  const open = async (offer: PartnerOffer) => {
    setOpening(offer.id);
    try {
      const url = await clickPartnerOffer(offer.id, tripId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error(t.marketplaceOpenFailed);
    } finally {
      setOpening(null);
    }
  };

  return (
    <ul className="space-y-3">
      {offers.map((offer) => (
        <li
          key={offer.id}
          className="rounded-lg border border-gray-200 bg-white p-4"
          data-testid="partner-offer"
        >
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {offer.partnerName}
          </p>
          <p className="mt-0.5 font-semibold text-gray-900">
            {offer.productName}
          </p>
          <p className="mt-1 text-sm text-gray-700">
            {offerDescription(offer, language)}
          </p>
          <p className="mt-2 text-xs text-gray-500">
            {fill(
              offer.disclosure === "per_visit"
                ? t.marketplaceDisclosurePerVisit
                : t.marketplaceDisclosurePerContract,
              offer.partnerName,
            )}
          </p>
          <button
            type="button"
            onClick={() => void open(offer)}
            disabled={opening === offer.id}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-60"
          >
            {fill(t.marketplaceVisit, offer.partnerName)}
            <ExternalLink className="h-3.5 w-3.5" />
          </button>
        </li>
      ))}
    </ul>
  );
};
